import { useCallback, useEffect, useState } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import { notificationsService, NotificationRecipientItem } from '../services/notifications'

export const useNotifications = () => {
  const { user } = useAuthContext()
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [items, setItems] = useState<NotificationRecipientItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    const [{ count }, { data }] = await Promise.all([
      notificationsService.fetchUnreadCount(user.id),
      notificationsService.fetchLatest(15, user.id)
    ])
    setUnreadCount(count || 0)
    setItems(data || [])
    setLoading(false)
  }, [user])

  const markAsRead = useCallback(async (recipientId: string) => {
    const { error } = await notificationsService.markAsRead(recipientId)
    if (!error) {
      setItems(prev => prev.map(it => it.id === recipientId ? { ...it, read_at: new Date().toISOString() } : it))
      setUnreadCount(prev => Math.max(0, prev - 1))
    }
  }, [])

  const deleteOne = useCallback(async (recipientId: string) => {
    const target = items.find(it => it.id === recipientId)
    const wasUnread = target && !target.read_at
    // Otimista
    setItems(prev => prev.filter(it => it.id !== recipientId))
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1))

    const { error } = await notificationsService.deleteRecipient(recipientId)
    if (error) {
      // Recarregar em caso de falha para manter consistência
      await load()
    }
  }, [items, load])

  const clearAll = useCallback(async () => {
    if (!user) return
    // Otimista
    setItems([])
    setUnreadCount(0)
    const { error } = await notificationsService.clearAllForUser(user.id)
    if (error) {
      await load()
    }
  }, [user, load])

  useEffect(() => {
    if (!user) return
    load()
    const unsubscribe = notificationsService.subscribeRealtime(user.id, () => {
      // Em cada mudança relevante, recarregar contagem e últimas
      load()
    })
    return unsubscribe
  }, [user, load])

  return {
    unreadCount,
    items,
    loading,
    refresh: load,
    markAsRead,
    deleteOne,
    clearAll
  }
}


